import {
  Drawer,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Portal,
  useBoolean,
  useMediaQuery,
} from '@chakra-ui/react';
import { Sidebar, Menu, MenuItem, CSSObject } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { GrillaEl, Imagen, PilaH } from '../atomos';
import Isotipo from '@/assets/icons/isotipo.svg';
import Logo from '@/assets/icons/factoring.svg';
import IconoDinamico from '../moleculas/IconoDinamico';
import { MenuItem as LinkBarraLateral } from '@/models/respuestas/usuario.model';
import { memo, useRef } from 'react';
import { MenuIcon } from 'lucide-react';

const Cabecera = ({ mostrar }: { mostrar: boolean }) => {
  return (
    <PilaH
      px={'24px'}
      py={4}
      flexWrap={'nowrap'}
      overflow={'hidden'}
      alignItems={'center'}
    >
      <Imagen src={Isotipo} h={'30px'} />
      <Imagen
        src={Logo}
        h={'25px'}
        w={'auto'}
        opacity={mostrar ? 1 : 0}
        transition={'opacity 300ms ease-in-out'}
      />
    </PilaH>
  );
};

const estilosBaseSidebar = {
  minHeight: '100dvh',
  height: '100%',
  backgroundColor: 'var(--layout)',
  borderColor: 'var(--compo-200)',

  '&:not(.ps-collapsed)': {
    boxShadow:
      '10px 0px 15px -3px rgba(0, 0, 0, 0.1), 4px 0px 6px -2px rgba(0, 0, 0, 0.05)',
  },
};

const estilosItemsMenu: CSSObject = {
  root: {
    color: 'var(--foreground-secondary)',
    ':hover:not(.ps-active)': {
      color: 'var(--foreground)',
    },
  },
  button: {
    transition: 'background 120ms ease-in-out',
    '&.ps-active': {
      color: 'var(--bGreen-600)',
    },
    ':hover': {
      background: 'var(--compo-100)',
    },
  },
  icon: {
    marginRight: '0',
    transition: 'color 120ms ease-in-out',
  },
};

const BarraLateral = memo(function BarraLateral({
  menu,
}: {
  menu: LinkBarraLateral[];
}) {
  const [mobile] = useMediaQuery('(max-width: 768px)');
  const [abierto, setApertura] = useBoolean(false);
  const location = useLocation();
  const drawerRef = useRef(null);

  if (mobile)
    return (
      <>
        <Portal>
          <IconButton
            icon={<MenuIcon />}
            background={'layout'}
            color={'foreground'}
            aria-label='menu-toggle'
            position={'fixed'}
            bottom={0}
            zIndex={1399}
            left={1}
            m={4}
            onClick={setApertura.toggle}
            outline={'2px solid'}
            outlineColor={'compo.200'}
            size={'lg'}
            _hover={{
              bg: 'compo.100',
              shadow: 'md',
            }}
          />
        </Portal>
        <Drawer isOpen={abierto} onClose={setApertura.off} placement='left'>
          <DrawerOverlay />
          <DrawerContent ref={drawerRef}>
            <Menu
              style={{
                alignItems: 'flex-end',
              }}
              menuItemStyles={{
                ...estilosItemsMenu,
                label: {
                  opacity: abierto ? 1 : 0,
                  transition:
                    'opacity 300ms ease-in-out, color 120ms ease-in-out',
                  '&.ps-active': {
                    fontWeight: '500',
                  },
                },
              }}
            >
              <Cabecera mostrar={abierto} />
              {/* Mapeo las opciones obtenidas desde la api */}
              {menu?.map((l) => (
                <MenuItem
                  active={location?.pathname === l.link}
                  component={<Link to={l.link} onClick={setApertura.off} />}
                  icon={<IconoDinamico nombre={l.icono} size={20} />}
                  key={`${l.nombre}-item`}
                >
                  {l.nombre}
                </MenuItem>
              ))}
            </Menu>
          </DrawerContent>
        </Drawer>
      </>
    );

  return (
    <GrillaEl colSpan={1}>
      <Sidebar
        rootStyles={estilosBaseSidebar}
        collapsed={!abierto}
        onMouseEnter={!mobile ? setApertura.on : undefined}
        onMouseLeave={!mobile ? setApertura.off : undefined}
      >
        <Menu
          menuItemStyles={{
            ...estilosItemsMenu,
            label: {
              opacity: abierto ? 1 : 0,
              transition: 'opacity 300ms ease-in-out, color 120ms ease-in-out',
              '&.ps-active': {
                fontWeight: '500',
              },
            },
          }}
        >
          <Cabecera mostrar={abierto} />
          {/* Mapeo las opciones obtenidas desde la api */}
          {menu?.map((l) => (
            <MenuItem
              active={location?.pathname === l.link}
              component={<Link to={l.link} />}
              icon={<IconoDinamico nombre={l.icono} size={20} />}
              key={`${l.nombre}-item`}
            >
              {l.nombre}
            </MenuItem>
          ))}
        </Menu>
      </Sidebar>
    </GrillaEl>
  );
});

export default BarraLateral;
